/**
 * Integration hooks for Mekke <-> Tabitto cross-app features
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../infrastructure/api/client";
import {
  ApiError,
  type IntegrationTripDto,
  type IntegrationTripDetailDto,
  type AddMekkeSpotToTripDto,
  type AddMekkeSpotToTripResponseDto,
} from "@tabipla/shared";

// Query key factory
const integrationKeys = {
  all: ["integration"] as const,
  trips: () => [...integrationKeys.all, "trips"] as const,
  tripDetail: (tripId: string) => [...integrationKeys.all, "trip", tripId] as const,
};

/**
 * Fetch user's trips for integration UI (dropdown selection)
 * @param enabled - Whether to fetch trips (use to defer fetching until dialog opens)
 */
export function useUserTrips(enabled = true) {
  return useQuery({
    queryKey: integrationKeys.trips(),
    queryFn: async () => {
      const response = await apiClient.get<IntegrationTripDto[]>("/api/integration/trips");
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch trips");
      }
      return response.data ?? [];
    },
    enabled,
  });
}

/**
 * Fetch trip detail with itineraries for integration UI
 */
export function useTripDetail(tripId: string | null) {
  return useQuery({
    queryKey: integrationKeys.tripDetail(tripId ?? ""),
    queryFn: async () => {
      if (!tripId) throw new Error("Trip ID is required");
      const response = await apiClient.get<IntegrationTripDetailDto>(
        `/api/integration/trips/${tripId}`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch trip detail");
      }
      return response.data;
    },
    enabled: !!tripId,
  });
}

/**
 * Add a Mekke spot to a Tabitto trip
 */
export function useAddMekkeSpotToTrip() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AddMekkeSpotToTripDto) => {
      const response = await apiClient.post<AddMekkeSpotToTripResponseDto>(
        "/api/integration/mekke-to-tabitto",
        data
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to add spot to trip");
      }
      return response.data;
    },
    onSuccess: () => {
      // Invalidate trip-related queries if needed
      queryClient.invalidateQueries({ queryKey: integrationKeys.trips() });
    },
  });
}
